import React, { useEffect, useState } from 'react';
import appapi from '../../../../api/apiClient';
import { CancelToken } from 'apisauce';
import { Box, Stack, TablePagination, Typography } from '@mui/material';
import Datatable from '../../../../components/DataTable';
import TablePaginationActions from '../../../../components/TablePaginationActions';
import { useTheme } from '@emotion/react';
import { useTranslation } from 'react-i18next';
import { useSelector } from 'react-redux';
import ArrowUpwardIcon from '@mui/icons-material/ArrowUpward';
import ArrowDownwardIcon from '@mui/icons-material/ArrowDownward';
import ViewTraderDetails from './ViewTraderDetails';

export default function PortfolioList() {
  const theme = useTheme();
  const text = theme.palette.primary.text;
  const { t } = useTranslation();

  const columns = [
    {
      id: 0,
      header: '#',
      accessor: 'Sno',
      width: '10%',
      default: true,
      columnEditable: false,
      activeColumn: true,
    },
    {
      id: 1,
      header: 'Trader name',
      accessor: 'traderName',
      width: '15%',
      default: true,
      columnEditable: false,
      activeColumn: true,
    },
    {
      id: 2,
      header: 'Buy Qty',
      accessor: 'buyQuantity',
      width: '15%',
      default: true,
      capitalize: true,
      columnEditable: false,
      activeColumn: true,
      Cell: (value) => (
        <Stack
          direction={'row'}
          alignItems={'center'}
        >
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={'#14285A'}
          >
            {value?.buyQuantity
              ? value?.buyQuantity?.toLocaleString()
              : '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 3,
      header: 'Sell Qty',
      accessor: 'sellQuantity',
      width: '20%',
      default: true,
      capitalize: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <Stack
          direction={'row'}
          alignItems={'center'}
        >
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={'#14285A'}
          >
            {value?.sellQuantity
              ? value?.sellQuantity?.toLocaleString()
              : '-'}
          </Typography>
        </Stack>
      ),
    },
    {
      id: 4,
      header: 'Overall G/L',
      accessor: 'overAllGainLoss',
      width: '20%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <Stack
          direction={'row'}
          alignItems={'center'}
        >
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={value?.priceFlag === 'increase' ? '#00A632' : value?.priceFlag === 'decrease' ? '#E89300' : text}
          >
            {(value?.overAllGainLoss || value.overAllGainLoss === 0) &&
              parseFloat(value?.overAllGainLoss)?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              }) || '-'}
          </Typography>
          {value?.priceFlag === 'decrease' ? <ArrowDownwardIcon style={{ width: '12px', height: '12px', color: '#E89300' }} /> : value?.priceFlag === 'increase' ? <ArrowUpwardIcon style={{ width: '12px', height: '12px', color: '#00A632' }} /> : null}
        </Stack>
      ),
    },
    {
      id: 5,
      header: 'Avg. Trade Price',
      accessor: 'avgTradePrice',
      width: '25%',
      default: true,
      columnEditable: true,
      activeColumn: true,
      Cell: (value) => (
        <Stack
          direction={'row'}
          alignItems={'center'}
          flex={1}
        >
          <Typography
            className="fw-regular ff-Roboto fs-12"
            color={text}
          >
            €{' '}
            {(value?.avgTradePrice &&
              parseFloat(value?.avgTradePrice)?.toLocaleString(undefined, {
                minimumFractionDigits: 2,
                maximumFractionDigits: 2,
              })) ||
              '0'}
          </Typography>
        </Stack>
      ),
    },
  ];

  const [payload, setPayload] = useState({
    page: 1,
    limit: 5,
    filter: null,
    totalLength: 0,
    paginationPage: 0,
    role: null,
    field: '_id',
    sortOrder: 'desc',
  });

  const [customerManagementColumn, setCustomerManagementColumn] = useState(columns);

  const [list, setList] = useState([]);
  const [selectedData, setSelectedData] = useState([]);
  const [isViewOpen, setIsViewOpen] = useState(false);
  const [isLoading, setIsLoading] = useState(true);

  let source = CancelToken.source();
  const { companyDetails, user } = useSelector((state) => state.auth);

  const handleChangePage = (event, newPage) => {
    setPayload((prevState) => ({ ...prevState, page: newPage + 1, paginationPage: newPage, filter: payload.filter }));
  };

  const handleChangeRowsPerPage = (event) => {
    setPayload((prev) => ({ ...prev, limt: parseInt(event.target.value, 5), paginationPage: 1, page: 1, filter: payload.filter }));
  };

  const handleGetPortfolioList = async (fromSearch = true) => {
    setIsLoading(true);
    source.cancel();
    source = CancelToken.source();
    const response = await appapi.get(
      `/trader/portfolioTraderList`,
      {
        pageNumber: payload.page,
        pageSize: payload.limit,
        companyId: user?.companyId,
      },
      { cancelToken: source.token },
    );
    if (response.ok) {
      const data = response.data;
      if (data.status === '7400') {
        let userData = data?.response?.response;
        userData.map((item, index) => {
          item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
          return item;
        });
        setList(userData);
        setPayload((prev) => ({ ...prev, totalLength: data?.response?.length, paginationPage: payload.page === 1 ? 0 : payload.page - 1 }));
        setIsLoading(false);
      } else {
        setList([]);
        setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 0 }));
        setIsLoading(false);
      }
    } else {
      setList([]);
      setPayload((prev) => ({ ...prev, totalLength: 0, paginationPage: 1 }));
      // setIsLoading(false);
    }
  };

  useEffect(() => {
    handleGetPortfolioList();
    return () => {
      if (source) {
        source.cancel();
      }
    };
    // eslint-disable-next-line
  }, [payload.page]);

  const selectRow = (sitem, sindex) => {
    const users = list.map((item, index) => {
      item.Sno = payload.limit * (payload.page === 1 ? 0 : payload.page - 1) + index + 1;
      item.selected = item._id === sitem._id;
      return item;
    });
    setSelectedData(sitem);
    setList(users);
    setIsViewOpen(true);
  };

  return (
    <>
      <Stack className="relative overflow-auto flex-auto  h-0 mt-[16px] px-[8px]">
        <Datatable
          tableHeadBgColor={'#EFEFEF'}
          columns={customerManagementColumn.filter((res) => res.activeColumn)}
          data={list || []}
          isLoading={isLoading}
          onClickRow={(item, index) => {
            selectRow(item, index);
          }}
        />
      </Stack>

      <Stack
        sx={{ width: '100%' }}
        className="relative pl-[16px]   inputFormStyle"
      >
        <Box className="flex  justify-end items-center h-[56px] w-[100%] p-[5px]">
          <TablePagination
            sx={{ borderBottom: 0 }}
            rowsPerPageOptions={[]}
            count={payload.totalLength ? payload.totalLength : 0}
            rowsPerPage={payload.limit ? payload.limit : 10}
            page={payload.paginationPage ? payload.paginationPage : 0}
            onPageChange={handleChangePage}
            onRowsPerPageChange={handleChangeRowsPerPage}
            ActionsComponent={TablePaginationActions}
          />
        </Box>
      </Stack>
      {isViewOpen && (
        <ViewTraderDetails
          portfolio={selectedData}
          modelOpen={isViewOpen}
          onModelHandleClose={(event) => {
            if (event) {
              setIsViewOpen(false);
              //   handleGetQuoteList();
            }
            setIsViewOpen(false);
          }}
        />
      )}
    </>
  );
}
